/**
 * Webex Contacts Manager
 * 
 * Standalone manager for Webex Contacts API integration.
 * Handles initialization, fetching, caching, and lookup operations.
 * Completely decoupled from DSS implementation.
 */

import { createContactsClient, IContacts } from '@webex/calling';
import type {
  WebexContact,
  ContactEntry,
  ContactsAPIResponse,
  WebexContactsCache,
  ContactsManagerConfig,
  ContactsCacheStats,
  ContactsPerformanceMetrics,
  ContactsTelemetryHandler,
  ContactsBatchLookupResult,
  ContactsLookupResult,
} from './types';
import { ContactsCacheManager } from './contactsCache';
import { dispatchContactsDataReady } from './types';
import { getPhoneLookupKeys, normalizePhone } from '../normalize';

/**
 * Check if a string appears to be encrypted (JWT format)
 */
function isEncrypted(value: string | undefined): boolean {
  if (!value) return false;
  // Encrypted fields start with 'eyJ' which is base64-encoded JWT header
  return value.startsWith('eyJ');
}

/**
 * Attempt to manually decrypt a field using Webex SDK's encryption service
 */
async function tryDecrypt(webex: any, value: string, encryptionKeyUrl?: string): Promise<string> {
  if (!value || !isEncrypted(value)) {
    return value;
  }

  try {
    // Try using internal encryption service if available
    const encryption = webex?.internal?.encryption;
    if (encryption && typeof encryption.decryptText === 'function') {
      console.log('[Enrichment] ContactsManager: Attempting manual decryption with encryption service');
      const decrypted = await encryption.decryptText(value, encryptionKeyUrl);
      return decrypted;
    }

    // Try using internal decryption methods if available
    if (webex?.internal?.decrypt && typeof webex.internal.decrypt === 'function') {
      console.log('[Enrichment] ContactsManager: Attempting manual decryption with internal.decrypt');
      const decrypted = await webex.internal.decrypt(value);
      return decrypted;
    }

    console.warn('[Enrichment] ContactsManager: No decryption service available');
    return value; // Return encrypted value
  } catch (error: any) {
    console.error('[Enrichment] ContactsManager: Manual decryption failed:', error.message);
    return value; // Return original encrypted value on failure
  }
}

/**
 * Mask phone number for logging (keep first 3 and last 2 digits)
 */
function maskPhone(phone: string): string {
  if (!phone || phone.length < 6) return '***';
  const normalized = normalizePhone(phone);
  return `${normalized.substring(0, 3)}...${normalized.substring(normalized.length - 2)}`;
}

/**
 * Main manager class for Webex Contacts API
 * Handles the complete lifecycle: fetch, cache, lookup, refresh
 */
export class WebexContactsManager {
  private webex: any;
  private contactsClient: IContacts | null = null;
  private callingClient: any | null = null;
  private cacheManager: ContactsCacheManager;
  private cache: WebexContactsCache | null = null;
  private userId: string;
  private isInitialized: boolean = false;
  private isFetching: boolean = false;
  private fetchPromise: Promise<void> | null = null;
  private refreshInterval: number | null = null;
  private telemetry?: ContactsTelemetryHandler;

  // Performance tracking
  private lastFetchDuration: number = 0;
  private lastBuildMapDuration: number = 0;
  private lookupTimes: number[] = [];

  constructor(webex: any, config: ContactsManagerConfig) {
    this.webex = webex;
    this.userId = config.userId;
    this.telemetry = config.enableTelemetry ? this.createTelemetryHandler() : undefined;

    // Initialize cache manager
    this.cacheManager = new ContactsCacheManager(
      config.userId,
      config.cacheTTL,
      this.telemetry
    );

    // Migrate old cache formats
    this.cacheManager.migrate();

    // Start auto-refresh if enabled
    if (config.autoRefresh) {
      this.startAutoRefresh();
    }
  }

  /**
   * Initialize contacts on app startup
   * - Check localStorage cache
   * - Fetch if cache missing or expired
   * - Store phone→contact map
   */
  async initialize(): Promise<void> {
    const startTime = Date.now();

    this.telemetry?.emit({
      type: 'init_started',
      userId: this.userId,
    });

    // Prevent concurrent initialization
    if (this.isFetching) {
      return this.fetchPromise || Promise.resolve();
    }

    if (this.isInitialized && !this.isExpired()) {
      return;
    }

    try {
      // 1. Try to load from cache
      const cached = this.cacheManager.load();

      if (cached && !this.cacheManager.isExpired(cached)) {
        // Validate that cached data is decrypted (not encrypted JWT tokens)
        const sampleEntries = Object.values(cached.phoneMap).slice(0, 3);
        const hasEncryptedData = sampleEntries.some(entry => 
          isEncrypted(entry.displayName) || isEncrypted(entry.firstName)
        );
        
        if (hasEncryptedData) {
          console.warn('[Enrichment] ContactsManager: ⚠️ Cached data contains encrypted entries, forcing refresh');
          this.cacheManager.clear();
          // Fall through to fetch fresh data
        } else {
          this.cache = cached;
          this.isInitialized = true;

          const duration = Date.now() - startTime;
          this.telemetry?.emit({
            type: 'init_success',
            duration,
            totalContacts: cached.totalContacts,
            cacheHit: true,
          });

          dispatchContactsDataReady(this.userId);
          // Stale-while-revalidate: show cached contacts immediately, then sync from API
          // so contacts added in Webex App appear without logout/cache clear.
          void this.syncFromApi().catch(() => undefined);
          return;
        }
      }
      
      if (cached) {
        // Cache expired, will fetch fresh
      }

      // 2. Cache miss or expired - fetch from API (no timeout wrapper - let SDK handle timing)
      await this.fetchAndCache();

      const duration = Date.now() - startTime;
      this.telemetry?.emit({
        type: 'init_success',
        duration,
        totalContacts: this.cache?.totalContacts || 0,
        cacheHit: false,
      });
    } catch (error: any) {
      const duration = Date.now() - startTime;
      console.error('[Enrichment] ContactsManager: ❌ INITIALIZE FAILED');
      console.error('[Enrichment] ContactsManager: Error message:', error.message);
      console.error('[Enrichment] ContactsManager: Error type:', error.constructor?.name);
      console.error('[Enrichment] ContactsManager: Full error:', error);
      console.error('[Enrichment] ContactsManager: Stack trace:', error.stack);
      console.error('[Contacts] Manager: Initialize failed', {
        error: error.message,
        duration,
      });

      this.telemetry?.emit({
        type: 'init_failed',
        error: error.message,
        duration,
      });

      // Don't throw - allow fallback to DSS-only mode
      this.isInitialized = false;
    }
  }

  /**
   * Fetch contacts from API and cache them
   */
  private async fetchAndCache(): Promise<void> {
    await this.ensureContactsClient();
    if (!this.contactsClient) {
      throw new Error('Contacts client not initialized');
    }

    this.isFetching = true;
    this.fetchPromise = (async () => {
      const startTime = Date.now();

      try {
        this.telemetry?.emit({
          type: 'fetch_started',
          userId: this.userId,
        });

        const apiStartTime = Date.now();

        // Call Contacts API - no timeout wrapper since API actually works
        const response = await this.contactsClient!.getContacts() as any as ContactsAPIResponse;
        
        const apiDuration = Date.now() - apiStartTime;
        this.lastFetchDuration = Date.now() - startTime;

        if (response.statusCode !== 200) {
          const error = new Error(`Contacts API returned ${response.statusCode}`);
          console.error('[Enrichment] ContactsManager: API call failed:', error.message);
          this.telemetry?.emit({
            type: 'fetch_failed',
            error: error.message,
            statusCode: response.statusCode,
          });
          throw error;
        }

        let contacts = response.data?.contacts || [];
        
        // Check if contacts are encrypted
        if (contacts.length > 0) {
          const firstContact = contacts[0];
          
          // Check if data appears to be encrypted
          const appearsEncrypted = isEncrypted(firstContact.displayName) || 
                                   isEncrypted(firstContact.firstName);
          
          if (appearsEncrypted) {
            console.error('[Enrichment] ContactsManager: ⚠️ WARNING: Contacts appear to be ENCRYPTED!');
            console.error('[Enrichment] ContactsManager: Attempting manual decryption...');
            
            // Attempt to manually decrypt contacts
            const decryptedContacts: WebexContact[] = [];
            let successCount = 0;
            let failCount = 0;
            
            for (const contact of contacts) {
              try {
                const encKeyUrl = (contact as any).encryptionKeyUrl || (response.data as any).encryptionKeyUrl;
                const decryptedContact = { ...contact };
                
                // Decrypt displayName if encrypted
                if (isEncrypted(contact.displayName)) {
                  decryptedContact.displayName = await tryDecrypt(this.webex, contact.displayName!, encKeyUrl);
                }
                
                // Decrypt firstName if encrypted
                if (isEncrypted(contact.firstName)) {
                  decryptedContact.firstName = await tryDecrypt(this.webex, contact.firstName!, encKeyUrl);
                }
                
                // Decrypt lastName if encrypted
                if (isEncrypted(contact.lastName)) {
                  decryptedContact.lastName = await tryDecrypt(this.webex, contact.lastName!, encKeyUrl);
                }
                
                // Decrypt phone numbers if encrypted
                if (contact.phoneNumbers) {
                  decryptedContact.phoneNumbers = await Promise.all(
                    contact.phoneNumbers.map(async (phone) => {
                      if (isEncrypted(phone.value)) {
                        const decryptedValue = await tryDecrypt(this.webex, phone.value, encKeyUrl);
                        return { ...phone, value: decryptedValue };
                      }
                      return phone;
                    })
                  );
                }
                
                // Check if decryption was successful
                const nowDecrypted = !isEncrypted(decryptedContact.displayName) && 
                                     !isEncrypted(decryptedContact.firstName);
                
                if (nowDecrypted) {
                  successCount++;
                  decryptedContacts.push(decryptedContact);
                } else {
                  failCount++;
                  // Keep original if decryption failed
                  decryptedContacts.push(contact);
                }
              } catch (error: any) {
                console.error('[Enrichment] ContactsManager: Failed to decrypt contact:', {
                  error: error.message
                });
                failCount++;
                decryptedContacts.push(contact); // Keep original
              }
            }
            
            console.log('[Enrichment] ContactsManager: Manual decryption results:', {
              total: contacts.length,
              success: successCount,
              failed: failCount
            });
            
            if (successCount > 0) {
              console.log('[Enrichment] ContactsManager: ✅ Successfully decrypted', successCount, 'contacts');
              contacts = decryptedContacts;
            } else {
              console.error('[Enrichment] ContactsManager: ❌ Manual decryption failed for all contacts');
            }
          } else {
            console.log('[Enrichment] ContactsManager: ✅ Contacts appear to be decrypted (plain text)');
          }
        }
        
        // Filter contacts with phone numbers
        const validContacts = contacts.filter(
          (c) => c.phoneNumbers && c.phoneNumbers.length > 0
        );

        // Build phone→contact map
        const buildMapStart = Date.now();
        const phoneMap = this.buildPhoneMap(validContacts);
        this.lastBuildMapDuration = Date.now() - buildMapStart;

        const phoneMapKeys = Object.keys(phoneMap);

        // Save to cache
        this.cache = this.cacheManager.save(phoneMap);
        this.isInitialized = true;

        this.telemetry?.emit({
          type: 'fetch_success',
          duration: this.lastFetchDuration,
          totalContacts: validContacts.length,
          totalPhones: Object.keys(phoneMap).length,
        });

        dispatchContactsDataReady(this.userId);
      } catch (error: any) {
        console.error('[Contacts] Manager: Fetch failed', {
          error: error.message,
        });

        this.telemetry?.emit({
          type: 'fetch_failed',
          error: error.message,
        });

        throw error;
      } finally {
        this.isFetching = false;
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Ensure contacts client is created after services catalog is ready
   */
  private async ensureContactsClient(): Promise<void> {
    if (this.contactsClient) {
      return;
    }

    if (!this.webex) {
      throw new Error('Webex SDK instance not available');
    }

    try {
      console.log('[Enrichment] ContactsManager: 🔧 Creating contacts client (direct path)...');
      
      const services = this.webex?.internal?.services;
      if (services && typeof services.waitForCatalog === 'function') {
        try {
          await services.waitForCatalog();
        } catch (error: any) {
          console.warn('[Enrichment] ContactsManager: services catalog wait failed, proceeding:', error?.message);
        }
      }

      const hasContactsService = Boolean(this.webex?.internal?.services?._activeServices?.contactsService);
      if (!hasContactsService) {
        const ready = await this.waitForContactsService(5000);
        if (!ready) {
          console.warn('[Enrichment] ContactsManager: contacts service not ready after wait; proceeding anyway');
        }
      }

      // createContactsClient will handle service discovery internally
      console.log('[Enrichment] ContactsManager: Creating contacts client directly...');
      const logger = this.webex.logger || console;
      this.contactsClient = createContactsClient(this.webex, logger);
      console.log('[Enrichment] ContactsManager: ✅ Contacts client created successfully');
    } catch (error: any) {
      console.error('[Enrichment] ContactsManager: ❌ Failed to create contacts client:', error.message);
      console.error('[Enrichment] ContactsManager: Error stack:', error.stack);
      this.contactsClient = null;
      throw error;
    }
  }

  private async waitForContactsService(timeoutMs: number): Promise<boolean> {
    const pollIntervalMs = 250;
    const start = Date.now();

    while (Date.now() - start < timeoutMs) {
      if (this.webex?.internal?.services?._activeServices?.contactsService) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    return false;
  }

  /**
   * Build denormalized phone→contact map for O(1) lookups
   */
  private buildPhoneMap(contacts: WebexContact[]): Record<string, ContactEntry> {
    const phoneMap: Record<string, ContactEntry> = {};
    let skippedEncrypted = 0;

    for (const contact of contacts) {
      if (!contact.phoneNumbers || contact.phoneNumbers.length === 0) {
        continue;
      }

      try {
        // Check if contact data is still encrypted (indicates decryption failure)
        const displayNameEncrypted = isEncrypted(contact.displayName);
        const firstNameEncrypted = isEncrypted(contact.firstName);
        const phoneEncrypted = isEncrypted(contact.phoneNumbers[0]?.value);
        
        if (displayNameEncrypted || firstNameEncrypted || phoneEncrypted) {
          skippedEncrypted++;
          continue;
        }
        
        // Extract display name
        const displayName =
          contact.displayName ||
          `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
          'Unknown';

        // Normalize all phone numbers
        const normalizedPhones = contact.phoneNumbers
          .map((p) => normalizePhone(p.value))
          .filter((p) => p.length > 0);

        if (normalizedPhones.length === 0) {
          continue;
        }

        // Find primary phone
        const primaryPhoneObj = contact.phoneNumbers.find((p) => p.primary);
        const primaryPhone = primaryPhoneObj
          ? normalizePhone(primaryPhoneObj.value)
          : undefined;

        // Create contact entry
        const entry: ContactEntry = {
          contactId: contact.contactId,
          displayName,
          firstName: contact.firstName,
          lastName: contact.lastName,
          contactType: contact.contactType,
          phones: normalizedPhones,
          primaryPhone,
          avatarURL: contact.avatarURL,
        };

        // Index by all phone numbers (denormalized for speed; + and no+ aliases)
        for (const normalizedPhone of normalizedPhones) {
          for (const key of getPhoneLookupKeys(normalizedPhone)) {
            phoneMap[key] = entry;
          }
        }
      } catch (error: any) {
        console.error('[Contacts] Manager: Failed to process contact', {
          contactId: contact.contactId,
          error: error.message,
        });

        this.telemetry?.emit({
          type: 'decryption_error',
          contactId: contact.contactId,
          error: error.message,
        });
      }
    }

    if (skippedEncrypted > 0) {
      console.error('[Enrichment] ContactsManager: ⚠️ Skipped', skippedEncrypted, 'encrypted contacts');
      console.error('[Enrichment] ContactsManager: Contacts are not being decrypted by the SDK!');
      console.error('[Enrichment] ContactsManager: Check KMS/encryption configuration.');
    }

    return phoneMap;
  }

  /**
   * Lookup contact by phone number
   * Returns undefined if not found or not initialized
   */
  lookupByPhone(phone: string): ContactsLookupResult | undefined {
    const lookupStart = Date.now();

    if (!this.isInitialized || !this.cache) {
      return undefined;
    }

    const normalizedPhone = normalizePhone(phone);
    let entry: ContactEntry | undefined;
    for (const key of getPhoneLookupKeys(normalizedPhone)) {
      entry = this.cache.phoneMap[key];
      if (entry) {
        break;
      }
    }

    const lookupTime = Date.now() - lookupStart;
    this.lookupTimes.push(lookupTime);
    if (this.lookupTimes.length > 100) {
      this.lookupTimes.shift(); // Keep last 100
    }

    if (entry) {
      this.telemetry?.emit({
        type: 'lookup_hit',
        phone: maskPhone(phone),
        displayName: entry.displayName,
      });

      return {
        contactId: entry.contactId,
        displayName: entry.displayName,
        firstName: entry.firstName,
        lastName: entry.lastName,
        contactType: entry.contactType,
        phones: entry.phones,
        avatarURL: entry.avatarURL,
        source: 'webex-contacts',
      };
    }

    this.telemetry?.emit({
      type: 'lookup_miss',
      phone: maskPhone(phone),
    });

    return undefined;
  }

  /**
   * Batch lookup multiple phones
   * More efficient than multiple single lookups
   */
  lookupMany(phones: string[]): ContactsBatchLookupResult {
    const found = new Map<string, ContactsLookupResult>();
    const notFound: string[] = [];

    for (const phone of phones) {
      const result = this.lookupByPhone(phone);
      if (result) {
        found.set(phone, result);
      } else {
        notFound.push(phone);
      }
    }

    return { found, notFound };
  }

  /**
   * Fetch latest contacts from API without clearing the in-memory cache first.
   * Used for background sync (stale-while-revalidate) and tab-focus refresh.
   */
  async syncFromApi(): Promise<void> {
    if (this.isFetching) {
      await (this.fetchPromise ?? Promise.resolve());
      return;
    }
    await this.fetchAndCache();
  }

  /**
   * Force refresh contacts from API
   */
  async refresh(): Promise<void> {
    this.clear();
    await this.fetchAndCache();
  }

  /**
   * Check if cache is expired
   */
  isExpired(): boolean {
    if (!this.cache) {
      return true;
    }
    return this.cacheManager.isExpired(this.cache);
  }

  /**
   * Get cache statistics
   */
  getStats(): ContactsCacheStats {
    const stats = this.cacheManager.getStats(this.cache);
    return stats;
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): ContactsPerformanceMetrics {
    const stats = this.getStats();
    const avgLookupTime =
      this.lookupTimes.length > 0
        ? this.lookupTimes.reduce((a, b) => a + b, 0) / this.lookupTimes.length
        : 0;

    return {
      fetchDuration: this.lastFetchDuration,
      buildMapDuration: this.lastBuildMapDuration,
      cacheSaveTime: 0, // Tracked by cache manager
      cacheLoadTime: 0, // Tracked by cache manager
      lookupAvgTime: avgLookupTime,
      memoryUsage: stats.cacheSize,
      totalContacts: stats.totalContacts,
      totalPhones: stats.totalPhones,
    };
  }

  /**
   * Clear cache and reset state
   */
  clear(): void {
    console.log('[Contacts] Manager: Clearing cache');
    this.cache = null;
    this.isInitialized = false;
    this.cacheManager.clear();
  }

  /**
   * Start auto-refresh timer
   */
  private startAutoRefresh(intervalMs: number = 60 * 60 * 1000): void {
    console.log('[Contacts] Manager: Starting auto-refresh', {
      intervalMs,
    });

    this.refreshInterval = window.setInterval(() => {
      if (this.isExpired()) {
        console.log('[Contacts] Manager: Auto-refresh triggered');
        this.refresh().catch((error) => {
          console.error('[Contacts] Manager: Auto-refresh failed', {
            error: error.message,
          });
        });
      }
    }, intervalMs);
  }

  /**
   * Stop auto-refresh timer
   */
  stopAutoRefresh(): void {
    if (this.refreshInterval !== null) {
      console.log('[Contacts] Manager: Stopping auto-refresh');
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Dispose and cleanup
   */
  dispose(): void {
    console.log('[Contacts] Manager: Disposing');
    this.stopAutoRefresh();
    this.clear();
    this.contactsClient = null;
  }

  /**
   * Create telemetry handler
   */
  private createTelemetryHandler(): ContactsTelemetryHandler {
    return {
      emit: (event) => {
        // Log to console for now
        // Can be extended to send to analytics service
        console.log('[Contacts] Telemetry:', event);
      },
    };
  }
}
